import { Trans } from "@lingui/macro";
import { captureException } from "@sentry/react";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { get } from "radash";
import type React from "react";
import { useEffect } from "react";
import {
  type ErrorResponse,
  Form,
  isRouteErrorResponse,
  useParams,
  useRevalidator,
  useRouteError,
} from "react-router";
import { $path } from "safe-routes";
import { useOptionalAuth } from "~/modules/auth/hooks";
import { useEnv } from "~/modules/env/useEnv";

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => React.JSX.Element | null;

export function getErrorMessage(error: unknown) {
  if (typeof error === "string") return error;

  if (Array.isArray(error)) {
    const textErrors: Array<string> = error
      .map((error: unknown) => {
        const message = getErrorMessage(error);
        if (typeof message === "string") {
          return message;
        }

        return undefined;
      })
      .filter(Boolean);

    if (textErrors.length > 0) {
      return textErrors.join("\n");
    }
  }

  const message = get(error, "data") || get(error, "message");

  if (typeof message === "string") {
    return message;
  }

  if (Array.isArray(message)) {
    return getErrorMessage(message);
  }

  const errors = get(error, "errors");

  if (Array.isArray(errors)) {
    return getErrorMessage(errors);
  }

  console.error(
    "Unable to get error message for error",
    JSON.stringify(error, null, 2),
  );
  return "Unknown Error";
}

const defaultStatusHandlers: Record<number, StatusHandler> = {
  401: () => (
    <div className="flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12 max-w-sm">
      <div className="flex flex-col gap-2.5 items-center">
        <h1 className="text-2xl text-primary font-bold max-w-xs">
          <Trans>You're not authorized to access this page.</Trans>
        </h1>
        <span className="text-primary text-base font-normal">
          <Trans>Please log in to your account to access this page.</Trans>
        </span>
      </div>
      <ErrorBoundaryActions />
    </div>
  ),
  403: () => (
    <div className="flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12 max-w-sm">
      <div className="flex flex-col gap-2.5 items-center">
        <h1 className="text-2xl text-primary font-bold max-w-xs">
          <Trans>You're not allowed to access this page.</Trans>
        </h1>
        <span className="text-primary text-base font-normal">
          <Trans>
            Ask your administrator to grant you access to this page.
          </Trans>
        </span>
      </div>
      <ErrorBoundaryActions />
    </div>
  ),
  404: () => (
    <div className="flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12 max-w-sm">
      <div className="flex flex-col gap-2.5 items-center">
        <h1 className="text-2xl text-primary font-bold max-w-xs">
          <Trans>Oops! Looks like this page hit a sour note. 🎶</Trans>
        </h1>
        <span className="text-primary text-base font-normal">
          <Trans>
            Don't worry, we'll get you back on track—just head to a safer place
            and let the music play!
          </Trans>
        </span>
      </div>
      <ErrorBoundaryActions />
    </div>
  ),
  500: () => (
    <div className="flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12 max-w-sm">
      <div className="flex flex-col gap-2.5 items-center">
        <h1 className="text-2xl text-primary font-bold max-w-xs">
          <Trans>Oops! Something broke...</Trans>
        </h1>
        <span className="text-primary text-base font-normal">
          <Trans>Try again, or head back to a safe zone</Trans>
        </span>
      </div>
      <ErrorBoundaryActions retryable />
    </div>
  ),
};

export function GeneralErrorBoundary({
  defaultStatusHandler = ({ error, params }) => {
    if (error.status in defaultStatusHandlers) {
      return defaultStatusHandlers[error.status]({ error, params });
    }

    return (
      <div className="flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12 max-w-sm">
        <div className="flex flex-col gap-2.5 items-center">
          <h1 className="text-2xl text-primary font-bold max-w-xs">
            <Trans>Oops! Something broke...</Trans>
          </h1>
          <span className="text-primary text-base font-normal">
            <Trans>Try again, or head back to a safe zone</Trans>
          </span>
        </div>
        {error instanceof Error && import.meta.env.MODE !== "production" && (
          <pre className="text-left whitespace-pre-wrap break-all text-lg mt-10">
            {error.data}
          </pre>
        )}
        <ErrorBoundaryActions retryable />
      </div>
    );
  },
  statusHandlers,
  unexpectedErrorHandler = (error) => {
    const showError =
      error instanceof Error && import.meta.env.MODE !== "production";

    return (
      <div
        className={classNames(
          "flex flex-col gap-8 h-full items-center justify-center text-center px-10 py-12",
          {
            "max-w-sm": !showError,
          },
        )}
      >
        <div className="flex flex-col gap-2.5 items-center">
          <h1 className="text-2xl text-primary font-bold max-w-xs">
            <Trans>An unexpected error occured.</Trans>
          </h1>
          <span className="text-primary text-base font-normal">
            <Trans>Try again, or head back to a safe zone</Trans>
          </span>
        </div>
        {showError && (
          <pre className="text-left whitespace-pre-wrap break-all text-lg mt-10">
            {error.stack}
          </pre>
        )}
        <ErrorBoundaryActions retryable className="max-w-sm" />
      </div>
    );
  },
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => React.JSX.Element | null;
}) {
  const error = useRouteError();
  const params = useParams();

  console.error(error);

  useEffect(() => {
    captureException(error);
  }, [error]);

  return isRouteErrorResponse(error)
    ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
        error,
        params,
      })
    : unexpectedErrorHandler(error);
}

type ErrorBoundaryActionsProps = {
  className?: string;
  retryable?: boolean;
};

function ErrorBoundaryActions({
  className,
  retryable,
}: ErrorBoundaryActionsProps) {
  const auth = useOptionalAuth();
  const revalidator = useRevalidator();
  const env = useEnv();

  if (auth) {
    return retryable ? (
      <div className={classNames("w-full grid items-center gap-3", className)}>
        <Button
          size="xl"
          variant="primary"
          onClick={() => revalidator.revalidate()}
          loading={revalidator.state === "loading"}
        >
          <Trans>Retry</Trans>
        </Button>
        <Button size="xl" variant="ghost" to={$path("/")} reloadDocument>
          <Trans>Go back to a safer place</Trans>
        </Button>
      </div>
    ) : (
      <Button
        size="xl"
        variant="primary"
        to={$path("/")}
        reloadDocument
        className={className}
      >
        <Trans>Go back to a safer place</Trans>
      </Button>
    );
  }

  return (
    <div
      className={classNames(
        "max-sm:w-full grid sm:grid-cols-2 items-center gap-3",
        className,
      )}
    >
      <Form
        method="post"
        id="form-login"
        action="/auth/login"
        className="hidden"
      />
      <Button
        size="xl"
        variant="primary"
        to={`${env?.AUTH_URL}/register`}
        reloadDocument
      >
        <Trans>Create an account</Trans>
      </Button>
      <Button size="xl" variant="secondary" type="submit" form="form-login">
        <Trans>Login</Trans>
      </Button>
    </div>
  );
}
